/**
 * wall-art-hero-swap.js
 *
 * Swaps the product listing's hero <img> (slot 1 of the gallery) when the
 * customer changes format / frame_color in the wall-art-picker.
 *
 * Reads:
 *   <script id="WallArtHeroImagesData-<section.id>" type="application/json">
 *     [
 *       { "product_type": "framed_art_print", "aspect_ratio": "2x3",
 *         "frame_color": "black", "url": "https://...", "alt": "...",
 *         "is_default": true },
 *       ...
 *     ]
 *   </script>
 *
 * Listens for `wallart:selection:changed` { format, size, frameColor, ... }.
 * Looks up the entry matching (product_type=format, frame_color=frameColor),
 * swaps the slot-1 <img>'s src/srcset/alt.
 *
 * If the metafield isn't populated, this script's <script> tag isn't
 * emitted by main-product.liquid — the existing wall-art-gallery-renderer.js
 * keeps doing browser-side compositing for everyone.
 */

(function () {
  'use strict';

  function readHeroData() {
    const tag = document.querySelector('script[id^="WallArtHeroImagesData-"]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent || 'null');
    } catch {
      return null;
    }
  }

  // Customer-side `frameColor` values from wall-art-picker.js: 'none'
  // (unframed types), or 'black' / 'white' / 'natural_oak' (framed types).
  // Map to the metafield's `frame_color` taxonomy.
  function frameColorKey(customerFrameColor) {
    if (!customerFrameColor || customerFrameColor === 'none') return 'unframed';
    return String(customerFrameColor);
  }

  function findEntry(heroData, format, frameColor) {
    if (!Array.isArray(heroData)) return null;
    const fc = frameColorKey(frameColor);
    for (const e of heroData) {
      if (!e) continue;
      if (e.product_type !== format) continue;
      if (String(e.frame_color) !== fc) continue;
      return e;
    }
    return null;
  }

  /** Find the listing's hero <img>: the active slide of the product media
   *  gallery. Falls back to the wall-art-slot-1 placeholder if no real
   *  product image is rendered (uncommon — the bulk renderer uploads the
   *  default hero as the Shopify product image). */
  function findHeroImageElement() {
    // Active slide first (Dawn's pattern).
    const activeSlide = document.querySelector(
      '.product__media-item.is-active img, ' +
      'media-gallery .product__media-item.is-active img',
    );
    if (activeSlide) return activeSlide;
    // Fallback: any product__media-item that contains an <img>.
    const fallback = document.querySelector(
      '.product__media-item img, media-gallery .product__media-item img',
    );
    return fallback ?? null;
  }

  function applyHero(entry) {
    const img = findHeroImageElement();
    if (!img || !entry || !entry.url) return;
    img.setAttribute('src', entry.url);
    if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
    if (entry.alt) img.setAttribute('alt', entry.alt);
    // If the <img> sits inside a <picture>, drop the <source> children so
    // the browser doesn't pick a stale responsive variant.
    const picture = img.closest && img.closest('picture');
    if (picture) {
      for (const s of picture.querySelectorAll('source')) s.remove();
    }
  }

  function init() {
    const heroData = readHeroData();
    if (!heroData) return;

    function applyFromSelection(detail) {
      if (!detail) return;
      const entry = findEntry(heroData, detail.format, detail.frameColor);
      if (entry) applyHero(entry);
    }

    // 1. Apply current selection if the picker has already published one.
    if (window.__wallArtCurrentSelection) {
      applyFromSelection(window.__wallArtCurrentSelection);
    }

    // 2. React to subsequent selection changes from the picker.
    document.addEventListener('wallart:selection:changed', (ev) => {
      applyFromSelection(ev && ev.detail);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
